import React, { useEffect, useState, useRef } from 'react';
import { Form, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { openModal } from '../../../../actions/modal.action';
import { useDispatch } from 'react-redux';

const NUMBERS = [2, 3, 4, 5, 6, 7, 8, 9, 10];
const DataBinning = ({ binningConfiguration, onBinningConfigurationChange }) => {
  const [enableMin, setEnableMin] = useState(false);
  const [bin, setBin] = useState(5);
  const [binInterval, setBinInterval] = useState(5);
  const [openBinMinValue, setOpenBinMinValue] = useState('');
  const [label, setLabel] = useState('');
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const target = useRef(null);

  const handleClose = () => setShow(false);

  const toggleMinimum = (enable) => {
    setEnableMin(enable);
    if (!enable) {
      setOpenBinMinValue('');
      onBinningConfigurationChange({
        ...binningConfiguration,
        openBinMinValue: undefined,
      });
    }
  }

  const changeOpenBinMinValue = (value) => {
    if (!/^\d+$/.test(value)) {
      setOpenBinMinValue(0);
      onBinningConfigurationChange({
        ...binningConfiguration,
        binCount: bin,
        openBinMinValue: 0,
      });
      return;
    }
    const maxValue = (bin * binInterval / 100) * 100;
    const min = Number(value);
    if (min >= maxValue) {
      openModal({
        className: 'modal-alert-width',
        button1Text: 'Okay',
        button1Action: 'closeModal',
        imageSrc: '/icons/location.png',
        body: `The Open Top Bin minimum value must be less than your data maximum for this question, which is ${maxValue}.`,
      })(dispatch);
      setOpenBinMinValue(0);
      return;
    }

    setOpenBinMinValue(min);
    onBinningConfigurationChange({
      ...binningConfiguration,
      binCount: bin,
      openBinMinValue: min,
    });
  }

  const changeLabel = (value) => {
    setLabel(value)
    onBinningConfigurationChange({
      ...binningConfiguration,
      label: value
    });
  }

  const binCountChange = (value) => {
    if (value.length > 20) return;
    setBin(value);
    onBinningConfigurationChange({
      ...binningConfiguration,
      binCount: Number(value)
    });
  }

  useEffect(() => {
    if (!binningConfiguration) return;
    setBin(binningConfiguration.binCount);
    setBinInterval(binningConfiguration.binInterval);
    setLabel(binningConfiguration.label || '');
    const min = binningConfiguration.openBinMinValue >= 0 ? binningConfiguration.openBinMinValue : 0;
    setOpenBinMinValue(min);
    setEnableMin(binningConfiguration.openBinMinValue >= 0);
  }, [binningConfiguration]);

  return (
    <div className='mt-3'>
      <div>
        <h4 className="mb-3 mt-3">Data Binning
          <OverlayTrigger
            target={target.current}
            show={show}
            onHide={handleClose}
            placement="top"
            overlay={
              <Tooltip>
                Change how response data is grouped by adjusting the number of bins that are used in the chart. Set an open top bin for all values that are higher than a set minimum value. <a target='__blank' href='https://docs.google.com/document/d/1FKL4kEzelhwXWj0LMQ3u4TpVItGevB2H2JwVdYo3sgM/edit?tab=t.0'>Learn more about binning</a>.
                <br />
                <br />
                <Button
                  variant="outline-secondary"
                  className="btn btn-link white mr-3 text-white"
                  onClick={() => setShow(false)}
                >
                  Close
                </Button>
              </Tooltip>
            }
            delay={300}
          >
            <FontAwesomeIcon
              ref={target}
              onMouseEnter={() => setShow(true)}
              className="cursor-pointer ml-2" size="1x" icon={faInfoCircle} />
          </OverlayTrigger>
        </h4>
        <div className='slider mb-3 mt-3'>
          <label className="">Number of Bins</label>
          <input
            type="range"
            value={bin}
            onChange={(e) => binCountChange(e.target.value)}
            className='w-100'
            min={2}
            max={10}
            step={1}
          />
          <div className='w-100 numbers'>
            {NUMBERS.map((n) => (
              <div key={n} className='lines'>
                <div className='line'></div>
                {n % 2 === 0 ? n : ''}
              </div>
            ))}
          </div>
          <div className='open-top-bins'>
            <label >Create Open Top  Bin</label>
            <Form.Check
              type="switch"
              id="open-top-switch"
              checked={enableMin}
              value={enableMin}
              onChange={(e) => toggleMinimum(e.target.checked)}
              className="open-top-switch"
            />
          </div>
          {enableMin && <>
            <div className="border-gray mt-3" />
            <div className='open-top-bins'>
              <label >Add Minimum Value</label>
              <Form.Control
                type="text"
                className="minimun-value"
                placeholder='Min'
                size='sm'
                value={openBinMinValue}
                onChange={(e) => setOpenBinMinValue(e.target.value)}
                onBlur={(e) => { changeOpenBinMinValue(e.target.value) }}
              />
            </div>
          </>}
        </div>
      </div>
      <div>
        <h4>Add Label</h4>
        <Form.Control
          className="mt-3"
          value={label}
          type="text"
          maxLength={20}
          onChange={(e) => { changeLabel(e.target.value) }}
        />
        <div className='text-right pr-2 pt-1'>
          {label.length}/20
        </div>
      </div>
    </div>
  );
};

DataBinning.propTypes = {
  onBinningConfigurationChange: PropTypes.func.isRequired,
  binningConfiguration: PropTypes.instanceOf(Object).isRequired,
};

export default DataBinning;
